/**
 * createVirtualBackgroundEffect: TFLite 모델 로드 및 JitsiStreamBackgroundEffect 생성 팩토리
 * 원본: wehago-conference/src/utils/JitsiVirtualBackground/index.js
 *
 * 사용법:
 *   const effect = await createVirtualBackgroundEffect({ backgroundType: 'blur', blurValue: 25 });
 *   if (effect) track.setEffect(effect);
 */
import { JitsiStreamBackgroundEffect, type VirtualBackgroundConfig } from './JitsiStreamBackgroundEffect';

const MODEL_PATH = `${import.meta.env.BASE_URL}libs/selfie_segmentation_landscape.tflite`;

// 세분화 차원 (모델 입력 해상도)
const SEGMENTATION_DIMENSIONS = {
  width: 256,
  height: 144,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let tflite: any = null;
let modelBuffer: ArrayBuffer | null = null;
let isWasmDisabled = false;

function withTimeout<T>(ms: number, promise: Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('TFLite init timeout')), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); }
    );
  });
}

async function loadTFLite(): Promise<void> {
  if (tflite) return;
  if (isWasmDisabled) throw new Error('WASM disabled');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  const TIMEOUT = 10000;

  // SIMD 지원 여부 확인 (WebAssembly.validate 사용, wasm-check 불필요)
  let simd = false;
  try {
    // WebAssembly SIMD validation test
    const simdTestBytes = new Uint8Array([
      0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3,
      2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
    ]);
    simd = WebAssembly.validate(simdTestBytes);
  } catch { /* empty */ }

  if (simd && typeof w.createTFLiteSIMDModule === 'function') {
    try {
      tflite = await withTimeout(TIMEOUT, w.createTFLiteSIMDModule());
      return;
    } catch {
      // SIMD 실패 시 non-SIMD로 폴백
      tflite = null;
    }
  }

  if (typeof w.createTFLiteModule === 'function') {
    tflite = await withTimeout(TIMEOUT, w.createTFLiteModule());
  } else {
    throw new Error('TFLite module not found on window. Check /tflite/tflite.js loading.');
  }
}

async function loadModel(): Promise<void> {
  if (modelBuffer) return;

  const response = await fetch(MODEL_PATH);
  if (!response.ok) throw new Error(`Failed to download tflite model: ${response.status}`);

  modelBuffer = await response.arrayBuffer();
  tflite.HEAPU8.set(new Uint8Array(modelBuffer), tflite._getModelBufferMemoryOffset());
  tflite._loadModel(modelBuffer.byteLength);
}

/**
 * TFLite 모델을 이용한 실제 배경 분리 효과 생성
 * @param config - 가상 배경 설정 (backgroundType, virtualSource, blurValue)
 * @returns JitsiStreamBackgroundEffect 인스턴스 or null (지원 불가/초기화 실패 시)
 */
export async function createVirtualBackgroundEffect(
  config: VirtualBackgroundConfig
): Promise<JitsiStreamBackgroundEffect | null> {
  if (config.backgroundType === 'none') return null;
  if (isWasmDisabled) {
    console.warn('[VBG] WASM disabled, virtual background unavailable');
    return null;
  }

  try {
    await loadTFLite();
    await loadModel();
  } catch (err) {
    console.warn('[VBG] TFLite/model load failed:', err);
    isWasmDisabled = true;
    return null;
  }

  const options = {
    ...SEGMENTATION_DIMENSIONS,
    virtualBackground: config,
  };

  return new JitsiStreamBackgroundEffect(tflite, options);
}
